<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  rejectNationalCodeAction,
  updateNationalCodeAction,
  verifyNationalCodeAction,
} from '@/api/functions/users/national-code-actions'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps({
  userData: { type: Object },
  uuid: { type: String, required: true },
})

const emit = defineEmits(['refreshUserData'])
const toast = useToast()

const showRejectModal = ref(false)
const showAcceptModal = ref(false)
const isShowEditNationalCode = ref(false)
const nationalCode = ref(props.userData?.document?.national_code || '')
const isSaving = ref(false)
const isRejecting = ref(false)

watch(
  () => props.userData,
  (newVal) => {
    nationalCode.value = newVal?.document?.national_code || ''
  },
  { deep: true, immediate: true }
)

const nationalCodeIsVerified = computed(() => {
  return props.userData?.document?.status === 'VERIFIED'
})

const nationalCodeIsRejected = computed(() => {
  return props.userData?.document?.status === 'REJECTED'
})

const saveMutation = useMutationApi({
  mutationFn: async () => {
    isSaving.value = true
    try {
      if (!nationalCode.value) {
        throw new Error('لطفاً کد ملی را وارد کنید')
      }
      // if (props.userData?.national_code) {
      //     return await updateNationalCodeAction(props.uuid, nationalCode.value);
      // }
      // return await verifyNationalCodeAction(props.uuid);
      return await updateNationalCodeAction(props.uuid, nationalCode.value)
    } finally {
      isSaving.value = false
    }
  },
  onMutate: () => {
    toast.info('در حال بروزرسانی...')
  },
  onSuccess: (response) => {
    toast.success(response.data?.meta?.message || 'عملیات با موفقیت انجام شد')
    emit('refreshUserData')
    isShowEditNationalCode.value = false
    // showAcceptModal.value = false;
  },

  onError: (error: unknown) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const rejectMutation = useMutationApi({
  mutationFn: async () => {
    isRejecting.value = true
    try {
      return await rejectNationalCodeAction(props.uuid)
    } finally {
      isRejecting.value = false
    }
  },
  onMutate: () => {
    toast.info('در حال رد کد ملی...')
  },
  onSuccess: (response) => {
    toast.success(response.data?.meta?.message || 'کد ملی با موفقیت رد شد')
    emit('refreshUserData')
    showRejectModal.value = false
    nationalCode.value = ''
  },
  onError: (error: unknown) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const verifyMutation = useMutationApi({
  mutationFn: async () => {
    isSaving.value = true
    try {
      return await verifyNationalCodeAction(props.uuid)
    } finally {
      isSaving.value = false
    }
  },
  onMutate: () => {
    toast.info('در حال تایید کد ملی...')
  },
  onSuccess: (response) => {
    toast.success(response.data?.meta?.message || 'کد ملی با موفقیت تایید شد')
    emit('refreshUserData')
    showAcceptModal.value = false
  },
  onError: (error: unknown) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleVerify = () => verifyMutation.mutate()
const handleSave = () => saveMutation.mutate()
const handleReject = () => rejectMutation.mutate()
</script>

<template>
    <div class="bg-white rounded-xl grid lg:grid-cols-3 my-6 lg:h-28 p-8 gap-10 lg:gap-6">
        <ConfirmModal v-model="showRejectModal" title="رد کد ملی" :message="'آیا از رد کد ملی اطمینان دارید؟'"
            confirm-text="بله رد شود" cancel-text="بازگشت" @confirm="handleReject" @cancel="showRejectModal = false" />

        <ConfirmModal v-model="showAcceptModal" title="تایید کد ملی" :message="'آیا از تایید کد ملی اطمینان دارید؟'"
            confirm-text="بله تایید شود" cancel-text="بازگشت" @confirm="handleVerify"
            @cancel="showAcceptModal = false" />

        <div class="w-full flex items-center justify-between text-sm">
            <span class="text-[#2d5396]">کد ملی:</span>
            <span v-if="!isShowEditNationalCode" class="font-fa text-[#2d5396]">{{ userData?.document?.national_code
            }}</span>
            <div v-else>
                <Input @keyup.enter="handleSave" v-model="nationalCode" name="amount" label="کد ملی جدید را وارد کنید"
                    type="email" class="h-11!" />
            </div>
        </div>

        <div class="w-full flex items-center justify-between text-sm">
            <span class="text-[#2d5396]">وضعیت کد ملی:</span>
            <span class="font-fa text-[#1b325a]">{{ nationalCodeIsVerified ? 'تایید شده' : 'تایید نشده' }}</span>
        </div>

        <div class="flex items-center justify-end">
            <Button @click="isShowEditNationalCode = !isShowEditNationalCode" :isAdmin="true"
                class="w-1/2 lg:w-auto rounded-[10px] px-5 h-10! py-0 text-[13px] font-normal me-2">
                ویرایش
            </Button>

            <Button @click="showAcceptModal = true" :isAdmin="true"
                :disabled="!nationalCode || nationalCodeIsVerified || isSaving || isRejecting" :loading="isSaving"
                class="w-1/2 lg:w-auto rounded-[10px] px-5 h-10! py-0 text-[13px] font-normal me-2">
                <span v-if="!isSaving">تایید</span>
                <span v-else>در حال بروزرسانی...</span>
            </Button>

            <Button @click="showRejectModal = true" :isAdmin="true"
                :disabled="!nationalCode || nationalCodeIsRejected || isSaving || isRejecting" :loading="isRejecting"
                class="w-1/2 lg:w-auto border-2 disabled:border-0 disabled:cursor-not-allowed rounded-[10px] px-4 h-10! py-0 text-[13px] font-normal me-2">
                <span v-if="!isRejecting">رد</span>
                <span v-else>در حال بروزرسانی...</span>
            </Button>
        </div>
    </div>
</template>
